import { Icon } from '@iconify/react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { SectionTitle } from 'modules/shared/components';
import { Table } from 'modules/shared/components/data-display';
import useAsyncData from 'modules/shared/hooks/useAsyncData';
import { formatDate } from 'modules/shared/utils';
import { useCallback, useMemo, useState } from 'react';
import IssueLetterModal from './IssueLetterModal';
import LetterViewModal from './LetterViewModal';
import { LetterRow, getLetters } from '../people.api';

/** The letters and certificates this employee has been issued, on their own record. */
export default function EmployeeLettersSection({
  employeeId,
  employeeName,
}: {
  employeeId: number;
  employeeName?: string;
}) {
  const [issuing, setIssuing] = useState(false);
  const [viewing, setViewing] = useState<LetterRow | null>(null);

  const fetcher = useCallback(() => getLetters(employeeId), [employeeId]);
  const { data, loading, refetch } = useAsyncData<LetterRow[]>(fetcher, [], {
    code: 'EMPLOYEE_LETTERS_FAILED',
    errorMessage: 'Failed to load letters',
    toastOnError: false,
  });

  const columns = useMemo(
    () => [
      { id: 'reference_no', dataKey: 'reference_no', label: 'Reference', align: 'left' as const },
      {
        id: 'letter_label',
        dataKey: 'letter_label',
        label: 'Type',
        align: 'left' as const,
        renderCell: (value: unknown) => <Chip size='small' label={String(value)} variant='outlined' />,
      },
      { id: 'subject', dataKey: 'subject', label: 'Subject', align: 'left' as const },
      {
        id: 'issued_on',
        dataKey: 'issued_on',
        label: 'Issued',
        align: 'center' as const,
        renderCell: (value: unknown) => (value ? formatDate(value as string) : '—'),
      },
      {
        id: 'actions',
        dataKey: 'id',
        label: '',
        align: 'center' as const,
        renderCell: (_v: unknown, row: LetterRow) => (
          <Tooltip title='View / print'>
            <IconButton size='small' onClick={() => setViewing(row)}>
              <Icon icon='mdi:file-document-outline' width={18} />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    []
  );

  return (
    <Stack gap={2}>
      <Divider />
      <SectionTitle
        title='Letters & certificates'
        action={
          <Button
            variant='outlined'
            size='small'
            startIcon={<Icon icon='mdi:plus' />}
            onClick={() => setIssuing(true)}
          >
            Issue letter
          </Button>
        }
      />

      <Table
        columns={columns}
        data={data}
        loading={loading}
        emptyMessage={`No letters issued to ${employeeName ?? 'this employee'} yet`}
      />

      <IssueLetterModal
        open={issuing}
        employeeId={employeeId}
        onClose={() => setIssuing(false)}
        onIssued={async (letter) => {
          await refetch();
          setViewing(letter);
        }}
      />

      <LetterViewModal letter={viewing} onClose={() => setViewing(null)} />
    </Stack>
  );
}
